// BYOA / BYOK shared vocabulary (CONTRACT §"Fase 1b"). Client-safe: no db, no node builtins —
// the settings form and the server module read the same provider table.
import type { RunBackend } from '#/lib/runner/types'
import type { StageSourceInfo } from '#/lib/ai/stage-policy'

export const AI_PROVIDERS = ['anthropic', 'openai', 'openrouter', 'custom'] as const
export type AiProvider = (typeof AI_PROVIDERS)[number]
export const isProvider = (s: unknown): s is AiProvider => typeof s === 'string' && (AI_PROVIDERS as readonly string[]).includes(s)

/** OpenAI-style base (ends with /v1). `custom` has none — the user must give one. */
export const PROVIDER_BASE_URL: Record<AiProvider, string> = {
  anthropic: 'https://api.anthropic.com/v1',
  openai: 'https://api.openai.com/v1',
  openrouter: 'https://openrouter.ai/api/v1',
  custom: '',
}

/** Suggestions only — the form always accepts a free-text model id. */
export const PROVIDER_MODELS: Record<AiProvider, string[]> = {
  anthropic: ['claude-opus-4-6', 'claude-sonnet-4-6', 'claude-haiku-4-5'],
  openai: ['gpt-5.6', 'gpt-5.6-mini', 'gpt-5.5'],
  openrouter: ['anthropic/claude-sonnet-4.6', 'openai/gpt-5.6', 'z-ai/glm-5'],
  custom: [],
}

/** Where the user gets a key — shown next to the field, never hardcoded in the component. */
export const PROVIDER_CONSOLE_URL: Record<AiProvider, string> = {
  anthropic: 'https://console.anthropic.com/settings/keys',
  openai: 'https://platform.openai.com/api-keys',
  openrouter: 'https://openrouter.ai/keys',
  custom: '',
}

/** Coding agents a BYOA key can drive. `codex` is BYOK-only (OpenAI / custom Responses endpoint), never Bystep credits. */
export const BYOA_BACKENDS: RunBackend[] = ['claude', 'codex', 'openclaw', 'hermes']

export interface AiConnectionDto {
  id: string
  label: string
  provider: AiProvider
  baseUrl: string
  model: string
  backendPref: RunBackend | null
  isDefault: boolean
  /** masked, e.g. `sk-…4f2a` — the value itself never reaches the browser */
  keyHint: string
  lastOkAt: string | null
  createdAt: string
}

export interface AiConnectionTestResult {
  ok: boolean
  /** short, plain reason when it failed — never the raw provider payload */
  detail: string
  model: string | null
  lastOkAt: string | null
  ms: number
}

export interface WorkspaceAiSelection {
  aiConnectionId: string | null
  stageConnectionId: string | null
  stageSource: StageSourceInfo
}

/** OpenAI-style base URL, always ending in `/v1`. */
export function apiBaseOf(provider: AiProvider, baseUrl: string | null): string {
  const raw = (baseUrl || PROVIDER_BASE_URL[provider] || '').trim().replace(/\/+$/, '')
  if (!raw) return ''
  return /\/v\d+$/.test(raw) ? raw : `${raw}/v1`
}

/** Anthropic-style base URL (no `/v1`) — what `ANTHROPIC_BASE_URL` expects. */
export const anthropicBaseOf = (provider: AiProvider, baseUrl: string | null) => apiBaseOf(provider, baseUrl).replace(/\/v\d+$/, '')
